// Game Day in a league with no opponents (scenarios Q4 and Q5, B-360, B-361):
// my week against the whole league. The score card says my score, my place
// this week and, in all-play, the week's record; under it the week's scores in
// order and my lineup. Nothing here asks who I am playing. It reads the
// scoreboard (`ScoreboardView.field`) and my lineup, the two reads the
// Scoreboard and My Team already make.

import { useState } from 'react';
import {
  noScoreOf,
  valueOf,
  type FieldRowView,
  type LineupRowView,
  type ScoreboardView,
} from '@fsp/domain';
import { useLineup, useMyTeamId, useScoreboard } from '../../data/hooks';
import { Detail, useAtLeast } from '../../data/detail';
import { LiveDot, ProTeamCode, ScoreNumber, TeamMark } from '../../kit';
import { Button, Caption, Text } from '../../kit/ui';
import { useChatDrawer } from '../../shell/chatDrawer';
import { useActiveLeague } from '../../data/LeaguesProvider';
import { useLeagueSource } from '../../data/leagueSource';
import { AdjustPointsSheet } from '../shared/AdjustPointsSheet';
import { useTier } from '../../shell/useTier';
import { SplitScreen } from '../../templates/SplitScreen';
import { FromLeague } from '../shared/FromLeague';
import { PlayerCard, type PlayerCardData } from '../shared/PlayerCard';
import { StatsNote } from '../shared/StatsNote';
import { num } from '../shared/notComputed';
import { FieldTable, cutLine, placeLine, weekRecord } from './FieldTable';
import '../../styles/screens/game-day.css';
import '../../styles/screens/league-screens.css';
import { useTools } from '../../data/tools';

export function FieldGameDay({ period }: { period: number | undefined }) {
  return (
    <FromLeague snapshot={useScoreboard(period)}>
      {(board) => <FieldBody board={board} />}
    </FromLeague>
  );
}

function FieldBody({ board }: { board: ScoreboardView }) {
  const tools = useTools();
  const myTeamId = useMyTeamId();
  const lineup = valueOf(useLineup(myTeamId, board.period.index));
  const chat = useChatDrawer();
  const phone = useTier() === 'phone';
  const showContext = useAtLeast('standard');
  const [card, setCard] = useState<PlayerCardData | null>(null);
  const rows = board.field ?? [];
  const mine = rows.find((r) => r.team.isMine) ?? null;
  const active = useActiveLeague();
  const allPlay = active?.league.format.id === 'all-play';
  // A COMMISSIONER'S ADJUSTMENT (J1), once the week has started, in a real
  // league: any team's week, each its own row.
  const canAdjust =
    useLeagueSource() === 'api' &&
    active?.league.isCommissioner === true &&
    rows.some((r) => r.status !== 'pre');
  const [adjusting, setAdjusting] = useState(false);

  const adjust = canAdjust ? (
    <div className="fsp-matchup-adjust">
      <Button variant="quiet" size="xs" icon="veto" onClick={() => setAdjusting(true)}>
        Adjust points
      </Button>
    </div>
  ) : null;
  // KNOCKED OUT OF AN ELIMINATION LEAGUE (Q1, B-364): no week of mine to
  // show, and the league's week goes on beside the card that says so.
  const myOut = board.out?.find((o) => o.team.isMine) ?? null;
  const header = (
    <>
      {mine === null ? (
        <div className="fsp-surface fsp-mcard fsp-field-card">
          <Text tone="secondary">
            {myOut !== null
              ? `${myOut.team.name} went out after Week ${myOut.period}. The rest of the league plays on.`
              : 'You have no team in this league.'}
          </Text>
        </div>
      ) : (
        <FieldCard row={mine} of={rows.length} allPlay={allPlay} board={board} />
      )}
      {adjust}
    </>
  );

  const openCard = (r: LineupRowView) =>
    r.player &&
    setCard({
      player: r.player,
      game: r.game,
      projection: r.projection,
      points: r.points,
      statLine: r.statLine,
      action: null,
    });

  const final = mine?.status === 'final';
  const myLineup =
    lineup === null ? (
      <div className="fsp-matchup-none">
        <Text tone="secondary">Your lineup is on its way.</Text>
      </div>
    ) : (
      <div>
        {lineup.starters.map((r, i) => {
          const none = noScoreOf(r);
          return (
            <div
              key={`${r.slot}-${i}`}
              className="fsp-row fsp-row-clickable fsp-field-starter"
              data-ux-convenience=""
              onClick={() => openCard(r)}
            >
              <span className="fsp-cap fsp-field-slot">{r.slot}</span>
              <div className="fsp-matchup-who">
                {r.player ? (
                  <>
                    <div className="fsp-name">
                      <span className="fsp-name-text">{r.player.name}</span>
                      <ProTeamCode code={r.player.proTeam} />
                    </div>
                    {r.statLine ? <div className="fsp-sub">{r.statLine}</div> : null}
                  </>
                ) : (
                  <div className="fsp-muted">Empty</div>
                )}
              </div>
              <div className="fsp-right fsp-num">
                {r.points !== null
                  ? r.points.toFixed(1)
                  : final && none !== null
                    ? (0).toFixed(1)
                    : (!showContext || !tools.projections) && r.game?.state === 'pre'
                      ? null
                      : '—'}
                {showContext && r.game?.state !== 'final' && tools.projections ? (
                  <div className="fsp-sub">proj {num(r.projection)}</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );

  const talk = (
    <div className="fsp-matchup-none">
      <Text tone="secondary" block>
        Talk it over in the league’s chat.
      </Text>
      <div className="fsp-matchup-talk">
        <Button size="xs" icon="chat" onClick={() => chat.setOpen(true)}>
          Open chat
        </Button>
      </div>
    </div>
  );

  const league = <FieldTable rows={rows} allPlay={allPlay} />;
  return (
    <>
      <SplitScreen
        status={header}
        lead={!phone}
        regions={[
          { id: 'league', title: 'This week', content: league },
          { id: 'lineup', title: 'Your lineup', content: myLineup },
          { id: 'talk', title: 'Talk', content: talk },
        ]}
        ratio={[5, 5]}
      />
      <PlayerCard data={card} onClose={() => setCard(null)} />
      {canAdjust && active !== null ? (
        <AdjustPointsSheet
          target={{
            period: board.period.index,
            // Mine first, so the sheet opens on my own team.
            sides: [...rows]
              .sort((x, y) => Number(y.team.isMine) - Number(x.team.isMine))
              .map((r) => ({ matchupId: r.id, team: r.team, score: r.score })),
          }}
          leagueId={active.league.id}
          opened={adjusting}
          onClose={() => setAdjusting(false)}
        />
      ) : null}
    </>
  );
}

/**
 * My week, on the card at the top: the score, and what it comes to — my place
 * this week, and in all-play the week's record. Nothing about an opponent.
 */
function FieldCard({
  row,
  of,
  allPlay,
  board,
}: {
  row: FieldRowView;
  of: number;
  allPlay: boolean;
  board: ScoreboardView;
}) {
  const tools = useTools();
  const place = placeLine(row, of);
  const live = row.status === 'live';
  return (
    <div className="fsp-surface fsp-mcard fsp-field-card">
      <div className="fsp-field-card-top">
        <TeamMark team={row.team} size={36} />
        <div className="fsp-field-who">
          <div className="fsp-name">
            <span className="fsp-name-text">{row.team.name}</span>
          </div>
          <div className="fsp-sub">{row.team.ownerName}</div>
        </div>
        <ScoreNumber value={row.score} />
      </div>
      <div className="fsp-mcard-foot">
        <div className="fsp-mcard-status">
          <span className="fsp-mcard-marks">
            {live && board.stats.state !== 'live' ? (
              <span className="fsp-mcard-final">In progress</span>
            ) : live ? (
              <LiveDot label="Live" />
            ) : row.status === 'final' ? (
              <span className="fsp-mcard-final">FINAL</span>
            ) : (
              <span className="fsp-muted">Not started</span>
            )}
            {place !== null ? (
              <span className="fsp-muted">
                {' · '}
                {place}
                {row.status === 'final' ? '' : ' so far'}
              </span>
            ) : null}
          </span>
        </div>
        {/* THE CUT (Q1, B-364), where an elimination week has one. */}
        {cutLine(row, board.field ?? []) !== null ? (
          <div className={`fsp-sub fsp-field-cut${row.cut === 'safe' ? '' : ' fsp-field-cut-bad'}`}>
            {cutLine(row, board.field ?? [])}
          </div>
        ) : null}
        {allPlay && place !== null ? (
          <Caption>
            {weekRecord(row)} this week{row.status === 'final' ? '' : ' so far'}
          </Caption>
        ) : null}
        {row.status !== 'final' ? (
          <Detail min="standard">
            <div className="fsp-sub fsp-num">
              {row.yetToPlay} yet to play
              {tools.projections ? ` · proj ${num(row.projected)}` : ''}
            </div>
          </Detail>
        ) : null}
        <StatsNote stats={board.stats} />
      </div>
    </div>
  );
}
